import { MRP_LOCATION_PUBLIC_LOCATION_ID } from "@/lib/mrp";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import type {
  WarehouseHubLocation,
  WarehouseRecountLocationSummary,
} from "@/lib/warehouseRecountApi";

/** MRP planning sites treated as primary warehouses on recount (SGS, Suntronic, Kodiak). */
export const WAREHOUSE_PRIMARY_HUB_LOCATION_IDS: readonly number[] = [
  MRP_LOCATION_PUBLIC_LOCATION_ID.sgs,
  MRP_LOCATION_PUBLIC_LOCATION_ID.suntronic,
  MRP_LOCATION_PUBLIC_LOCATION_ID.kodiak,
];

const PRIMARY_WAREHOUSE_ID_SET = new Set<number>(WAREHOUSE_PRIMARY_HUB_LOCATION_IDS);

export function isPrimaryWarehouseHubLocationId(hubLocationId: number): boolean {
  return PRIMARY_WAREHOUSE_ID_SET.has(hubLocationId);
}

export function primaryWarehouseHubLocationIdsSorted(
  locations: readonly WarehouseHubLocation[],
): number[] {
  const primaryIdSet = new Set(WAREHOUSE_PRIMARY_HUB_LOCATION_IDS);
  return locations
    .filter((location) => primaryIdSet.has(location.hub_location_id))
    .sort((left, right) =>
      warehouseHubLocationLabel(left).localeCompare(warehouseHubLocationLabel(right)),
    )
    .map((location) => location.hub_location_id);
}

const WAREHOUSE_LOCATION_SEPARATOR_VALUE = "__warehouse_location_separator__";

export function warehouseHubLocationLabel(location: WarehouseHubLocation): string {
  const name = location.location_name?.trim();
  if (name != null && name !== "") {
    return name;
  }
  return `Location ${String(location.hub_location_id)}`;
}

export function buildWarehouseHubLocationSelectOptions(
  locations: readonly WarehouseHubLocation[],
): SearchableSelectOption[] {
  const byId = new Map(locations.map((location) => [location.hub_location_id, location]));
  const primary: SearchableSelectOption[] = [];
  for (const hubLocationId of primaryWarehouseHubLocationIdsSorted(locations)) {
    const location = byId.get(hubLocationId);
    if (location == null) continue;
    primary.push({
      value: String(location.hub_location_id),
      label: warehouseHubLocationLabel(location),
    });
  }
  const primaryIdSet = new Set(WAREHOUSE_PRIMARY_HUB_LOCATION_IDS);
  const other = locations
    .filter((location) => !primaryIdSet.has(location.hub_location_id))
    .map((location) => ({
      value: String(location.hub_location_id),
      label: warehouseHubLocationLabel(location),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  if (other.length === 0) {
    return primary;
  }
  if (primary.length === 0) {
    return other;
  }
  return [
    ...primary,
    {
      value: WAREHOUSE_LOCATION_SEPARATOR_VALUE,
      label: "Other sites",
      disabled: true,
    },
    ...other,
  ];
}

export function filterAndSortWarehouseRecountLocationSummaries(
  rows: readonly WarehouseRecountLocationSummary[],
  options: { showNonPrimarySites: boolean },
): WarehouseRecountLocationSummary[] {
  const filtered = options.showNonPrimarySites
    ? [...rows]
    : rows.filter((row) => isPrimaryWarehouseHubLocationId(row.hub_location_id));
  const primaryRows = filtered
    .filter((row) => isPrimaryWarehouseHubLocationId(row.hub_location_id))
    .sort((left, right) => locationSummaryLabel(left).localeCompare(locationSummaryLabel(right)));
  const otherRows = filtered
    .filter((row) => !isPrimaryWarehouseHubLocationId(row.hub_location_id))
    .sort((left, right) => locationSummaryLabel(left).localeCompare(locationSummaryLabel(right)));
  return [...primaryRows, ...otherRows];
}

function locationSummaryLabel(row: WarehouseRecountLocationSummary): string {
  const name = row.location_name?.trim();
  if (name != null && name !== "") {
    return name;
  }
  return `Location ${String(row.hub_location_id)}`;
}
